import {
  AppstoreOutlined,
  BarChartOutlined,
  CreditCardOutlined,
  DashboardOutlined,
  DownloadOutlined,
  DollarOutlined,
  FallOutlined,
  GiftOutlined,
  InboxOutlined,
  LineChartOutlined,
  PercentageOutlined,
  ReloadOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  ShopOutlined,
  TagOutlined,
  TeamOutlined,
  UserAddOutlined,
  UserOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { Button, Card, Col, DatePicker, Row, Select, Tabs } from 'antd';
import type { Dayjs } from 'dayjs';
import type { ColumnsType } from 'antd/es/table/interface';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useGetWarehousesQuery } from '@/modules/inventory/inventoryApi';
import {
  useGetCustomerReportQuery,
  useGetInventoryReportQuery,
  useGetOverviewReportQuery,
  useGetSalesReportQuery,
} from '@/modules/reports/reportsApi';
import { useAppSelector } from '@/store/hooks';
import type { ReportTab } from '@/types/report';
import { downloadAuthenticatedFile } from '@/utils/downloadFile';
import { formatInr, formatReportDecimal, formatReportInr, formatReportPercent } from '@/utils/currency';
import { formatDateTime } from '@/utils/date';
import { ucfirst } from '@/utils/string';
import { BreakdownBarChart, BreakdownPieChart, TrendLineChart } from './ReportCharts';
import { ReportSection } from './components/ReportSection';
import { ReportTable } from './components/ReportTable';
import { StatCard } from './components/StatCard';
import {
  breakdownColumns,
  customersBySalesColumns,
  inventoryColumns,
  topProductColumns,
} from './reportColumns';
import { useReportQueryParams } from './hooks/useReportQueryParams';
import styles from './Reports.module.scss';

export default function Reports() {
  const notify = useNotify();
  const accessToken = useAppSelector((state) => state.auth.accessToken);
  const { state, apiParams, dateRangeValue, patchParams, resetFilters } = useReportQueryParams();
  const { data: warehouses = [] } = useGetWarehousesQuery();
  const [isExporting, setIsExporting] = useState(false);

  const reportQueryOptions = {
    refetchOnMountOrArgChange: true,
  } as const;

  const overviewQuery = useGetOverviewReportQuery(apiParams, {
    ...reportQueryOptions,
    skip: state.tab !== 'overview',
  });
  const salesQuery = useGetSalesReportQuery(apiParams, {
    ...reportQueryOptions,
    skip: state.tab !== 'sales',
  });
  const inventoryQuery = useGetInventoryReportQuery(apiParams, {
    ...reportQueryOptions,
    skip: state.tab !== 'inventory',
  });
  const customerQuery = useGetCustomerReportQuery(apiParams, {
    ...reportQueryOptions,
    skip: state.tab !== 'customers',
  });

  const filterKey = `${state.dateFrom}-${state.dateTo}-${state.groupBy}-${state.warehouseId}`;

  const isFetching =
    overviewQuery.isFetching ||
    salesQuery.isFetching ||
    inventoryQuery.isFetching ||
    customerQuery.isFetching;

  useGlobalLoader(isFetching);

  const exportQuery = useMemo(() => {
    const params = new URLSearchParams();
    params.set('type', state.tab);
    if (state.dateFrom) params.set('dateFrom', state.dateFrom);
    if (state.dateTo) params.set('dateTo', state.dateTo);
    if (state.groupBy) params.set('groupBy', state.groupBy);
    if (state.warehouseId) params.set('warehouseId', state.warehouseId);
    params.set('limit', '25');
    return `?${params.toString()}`;
  }, [state.dateFrom, state.dateTo, state.groupBy, state.tab, state.warehouseId]);

  const handleExport = useCallback(async () => {
    if (!accessToken) {
      notify.error('You are not authenticated.');
      return;
    }

    try {
      setIsExporting(true);
      const filename = await downloadAuthenticatedFile(
        `/reports/export${exportQuery}`,
        accessToken,
        `betiyan-${state.tab}-report.xlsx`,
      );
      notify.success(`Downloaded ${filename}`);
    } catch {
      notify.error('Could not export report.');
    } finally {
      setIsExporting(false);
    }
  }, [accessToken, exportQuery, notify, state.tab]);

  const recentOrderColumns = useMemo<ColumnsType<NonNullable<typeof salesQuery.data>['recentOrders'][number]>>(
    () => [
      {
        title: '#',
        key: 'rank',
        width: 44,
        align: 'center',
        render: (_value, _record, index) => index + 1,
      },
      {
        title: 'ORDER #',
        dataIndex: 'orderNumber',
        key: 'orderNumber',
        width: 110,
        render: (value: string, record) => (
          <Link to={`${ROUTES.ORDERS}/${record.id}`} className="report-customer-link">
            {value}
          </Link>
        ),
      },
      { title: 'CUSTOMER', dataIndex: 'customerName', key: 'customerName', ellipsis: true },
      {
        title: 'TOTAL',
        dataIndex: 'grandTotal',
        key: 'grandTotal',
        align: 'right',
        width: 100,
        render: (value?: number) => formatReportInr(value),
      },
      {
        title: 'STATUS',
        dataIndex: 'orderStatus',
        key: 'orderStatus',
        width: 96,
        render: (value: string) => ucfirst(value),
      },
      {
        title: 'PAYMENT',
        dataIndex: 'paymentStatus',
        key: 'paymentStatus',
        width: 96,
        render: (value: string) => ucfirst(value),
      },
      {
        title: 'PLACED',
        dataIndex: 'placedAt',
        key: 'placedAt',
        width: 138,
        render: (value: string) => formatDateTime(value),
      },
    ],
    [],
  );

  const customerSalesChartData = useMemo(
    () =>
      (salesQuery.data?.customersBySales ?? []).slice(0, 10).map((customer) => ({
        key: customer.customerId,
        label: customer.customerName,
        value: customer.totalSpent,
        count: customer.totalOrders,
      })),
    [salesQuery.data?.customersBySales],
  );

  const overviewContent = overviewQuery.data ? (
    <div key={filterKey} className={styles.tabContent}>
      <Row gutter={[12, 12]} className={styles.statsRow}>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={0} tone="primary" icon={<DollarOutlined />} label="Revenue" value={formatInr(overviewQuery.data.sales.totalRevenue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={1} tone="accent" icon={<ShoppingCartOutlined />} label="Orders" value={String(overviewQuery.data.sales.totalOrders)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={2} tone="success" icon={<RiseOutlined />} label="AOV" value={formatInr(overviewQuery.data.sales.averageOrderValue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={3} tone="warning" icon={<AppstoreOutlined />} label="Items sold" value={String(overviewQuery.data.sales.totalItemsSold)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={4} tone="primary" icon={<InboxOutlined />} label="Stock value" value={formatInr(overviewQuery.data.inventory.totalStockValue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={5} tone="accent" icon={<UserAddOutlined />} label="New customers" value={String(overviewQuery.data.customers.newCustomers)} />
        </Col>
      </Row>

      <ReportSection
        sectionKey="overview-charts"
        title="Trend charts"
        subtitle="Revenue and order movement over the selected period"
        icon={<LineChartOutlined />}
        preview="2 charts"
      >
        <div className={styles.chartsGrid}>
          <TrendLineChart title="Revenue trend" data={overviewQuery.data.revenueTrend ?? []} valueLabel="Revenue" currency />
          <TrendLineChart title="Orders trend" data={overviewQuery.data.ordersTrend ?? []} valueLabel="Orders" />
        </div>
      </ReportSection>

      <ReportTable
        sectionKey="overview-top-products"
        title="Top products by revenue"
        subtitle="Best performing products in the selected period"
        icon={<ShopOutlined />}
        rowKey="sku"
        columns={topProductColumns}
        dataSource={overviewQuery.data.topProducts ?? []}
      />

      <ReportTable
        sectionKey="overview-customers-by-sales"
        title="Customers by sales"
        subtitle="Highest spending customers ranked by revenue"
        icon={<TeamOutlined />}
        rowKey="customerId"
        columns={customersBySalesColumns}
        dataSource={overviewQuery.data.topCustomers ?? []}
      />
    </div>
  ) : null;

  const salesContent = salesQuery.data ? (
    <div key={filterKey} className={styles.tabContent}>
      <Row gutter={[12, 12]} className={styles.statsRow}>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={0} tone="primary" icon={<DollarOutlined />} label="Revenue" value={formatInr(salesQuery.data.summary.totalRevenue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={1} tone="accent" icon={<ShoppingCartOutlined />} label="Orders" value={String(salesQuery.data.summary.totalOrders)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={2} tone="success" icon={<RiseOutlined />} label="AOV" value={formatInr(salesQuery.data.summary.averageOrderValue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={3} tone="warning" icon={<AppstoreOutlined />} label="Items sold" value={String(salesQuery.data.summary.totalItemsSold)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard
            index={4}
            tone="primary"
            icon={<TagOutlined />}
            label="Discounts"
            value={formatInr(salesQuery.data.summary.totalDiscount)}
            hint={`${salesQuery.data.summary.paidOrders} paid orders`}
          />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard
            index={5}
            tone="success"
            icon={<PercentageOutlined />}
            label="Conversion"
            value={formatReportPercent(salesQuery.data.summary.conversionRate)}
            hint={`${salesQuery.data.summary.cancelledOrders} cancelled`}
          />
        </Col>
      </Row>

      <ReportSection
        sectionKey="sales-charts"
        title="Sales analytics"
        subtitle="Trends, customer spend, payment mix, and geographic breakdown"
        icon={<BarChartOutlined />}
        preview="6 charts"
      >
        <div className={styles.chartsGrid}>
          <TrendLineChart title="Revenue trend" data={salesQuery.data.revenueTrend ?? []} valueLabel="Revenue" currency />
          <TrendLineChart title="Orders trend" data={salesQuery.data.ordersTrend ?? []} valueLabel="Orders" />
          <BreakdownBarChart title="Customers by sales (top 10)" data={customerSalesChartData} />
          <BreakdownBarChart title="Revenue by payment method" data={salesQuery.data.paymentMethodBreakdown ?? []} />
          <BreakdownPieChart title="Orders by status" data={salesQuery.data.orderStatusBreakdown ?? []} />
          <BreakdownBarChart title="Sales by city" data={salesQuery.data.salesByCity ?? []} />
        </div>
      </ReportSection>

      <ReportTable
        sectionKey="sales-customers-by-sales"
        title="Customers by sales"
        subtitle="Full customer sales performance with revenue share, discounts, and last order date"
        icon={<TeamOutlined />}
        rowKey="customerId"
        columns={customersBySalesColumns}
        dataSource={salesQuery.data.customersBySales ?? []}
      />

      <ReportTable
        sectionKey="sales-top-products"
        title="Top products by revenue"
        subtitle="Products driving the most sales in this period"
        icon={<ShopOutlined />}
        defaultExpanded={false}
        rowKey="sku"
        columns={topProductColumns}
        dataSource={salesQuery.data.topProducts ?? []}
      />

      {(salesQuery.data.couponBreakdown?.length ?? 0) > 0 ? (
        <ReportTable
          sectionKey="sales-coupons"
          title="Coupon performance"
          subtitle="Revenue and order count by coupon code"
          icon={<GiftOutlined />}
          defaultExpanded={false}
          rowKey="key"
          columns={breakdownColumns}
          dataSource={salesQuery.data.couponBreakdown ?? []}
        />
      ) : null}

      <ReportTable
        sectionKey="sales-recent-orders"
        title="Recent orders"
        subtitle="Latest orders placed in the selected date range"
        icon={<CreditCardOutlined />}
        defaultExpanded={false}
        rowKey="id"
        columns={recentOrderColumns}
        dataSource={salesQuery.data.recentOrders ?? []}
      />
    </div>
  ) : null;

  const inventoryContent = inventoryQuery.data ? (
    <div key={filterKey} className={styles.tabContent}>
      <Row gutter={[12, 12]} className={styles.statsRow}>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={0} tone="primary" icon={<InboxOutlined />} label="Units sold" value={String(inventoryQuery.data.summary.totalStockQuantity)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={1} tone="accent" icon={<DollarOutlined />} label="Sales value" value={formatInr(inventoryQuery.data.summary.totalStockValue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={2} tone="success" icon={<AppstoreOutlined />} label="SKUs sold" value={String(inventoryQuery.data.summary.totalSkus)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={3} tone="warning" icon={<WarningOutlined />} label="Low stock" value={String(inventoryQuery.data.summary.lowStockItems)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={4} tone="danger" icon={<FallOutlined />} label="Out of stock" value={String(inventoryQuery.data.summary.outOfStockItems)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard
            index={5}
            tone="success"
            icon={<ShoppingCartOutlined />}
            label="Sold in period"
            value={String(inventoryQuery.data.summary.soldQuantity)}
            hint={formatInr(inventoryQuery.data.summary.soldRevenue)}
          />
        </Col>
      </Row>

      <ReportSection
        sectionKey="inventory-charts"
        title="Inventory analytics"
        subtitle="Units sold and stock health for SKUs sold in the selected period"
        icon={<BarChartOutlined />}
        preview="2 charts"
      >
        <div className={styles.chartsGrid}>
          <BreakdownBarChart title="Stock by warehouse" data={inventoryQuery.data.stockByWarehouse ?? []} currency={false} />
          <BreakdownPieChart title="Stock health" data={inventoryQuery.data.stockStatusBreakdown ?? []} />
        </div>
      </ReportSection>

      <ReportTable
        sectionKey="inventory-top-skus"
        title="Top selling SKUs"
        subtitle="SKUs with highest sold quantity and revenue"
        icon={<ShopOutlined />}
        rowKey="sku"
        columns={topProductColumns}
        dataSource={inventoryQuery.data.topSellingSkus ?? []}
      />

      <ReportTable
        sectionKey="inventory-low-stock"
        title="Low / out of stock"
        subtitle="Items that need replenishment attention"
        icon={<WarningOutlined />}
        defaultExpanded={false}
        rowKey={(record) => `${record.sku}-${record.warehouseName}`}
        columns={inventoryColumns}
        dataSource={inventoryQuery.data.lowStockItems ?? []}
      />

      <ReportTable
        sectionKey="inventory-valuation"
        title="Inventory valuation"
        subtitle="Stock on hand ranked by inventory value"
        icon={<InboxOutlined />}
        defaultExpanded={false}
        rowKey={(record) => `${record.sku}-${record.warehouseName}-full`}
        columns={inventoryColumns}
        dataSource={inventoryQuery.data.inventoryItems ?? []}
      />
    </div>
  ) : null;

  const customerContent = customerQuery.data ? (
    <div key={filterKey} className={styles.tabContent}>
      <Row gutter={[12, 12]} className={styles.statsRow}>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={0} tone="primary" icon={<TeamOutlined />} label="Customers in period" value={String(customerQuery.data.summary.totalCustomers)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={1} tone="accent" icon={<UserAddOutlined />} label="New customers" value={String(customerQuery.data.summary.newCustomers)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={2} tone="success" icon={<UserOutlined />} label="Active buyers" value={String(customerQuery.data.summary.activeCustomers)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={3} tone="warning" icon={<RiseOutlined />} label="Repeat buyers" value={String(customerQuery.data.summary.repeatCustomers)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard index={4} tone="primary" icon={<DollarOutlined />} label="Avg LTV" value={formatInr(customerQuery.data.summary.averageLifetimeValue)} />
        </Col>
        <Col xs={12} sm={8} lg={4} className={styles.statCol}>
          <StatCard
            index={5}
            tone="success"
            icon={<ShoppingCartOutlined />}
            label="Orders / customer"
            value={formatReportDecimal(customerQuery.data.summary.averageOrdersPerCustomer)}
          />
        </Col>
      </Row>

      <ReportSection
        sectionKey="customer-charts"
        title="Customer analytics"
        subtitle="Signups, demographics, and top spenders"
        icon={<LineChartOutlined />}
        preview="3 charts"
      >
        <div className={styles.chartsGrid}>
          <TrendLineChart title="Customer signups" data={customerQuery.data.signupTrend ?? []} valueLabel="Signups" />
          <BreakdownPieChart title="Gender breakdown" data={customerQuery.data.genderBreakdown ?? []} />
          <BreakdownBarChart
            title="Customers by sales (top 10)"
            data={(customerQuery.data.customersBySales ?? []).slice(0, 10).map((customer) => ({
              key: customer.customerId,
              label: customer.customerName,
              value: customer.totalSpent,
              count: customer.totalOrders,
            }))}
          />
        </div>
      </ReportSection>

      <ReportTable
        sectionKey="customer-by-sales"
        title="Customers by sales"
        subtitle="Customer lifetime sales ranked by total revenue in the selected period"
        icon={<TeamOutlined />}
        rowKey="customerId"
        columns={customersBySalesColumns}
        dataSource={customerQuery.data.customersBySales ?? []}
      />

      <ReportTable
        sectionKey="customer-segments"
        title="Customer segments"
        subtitle="New, repeat, and active buyer distribution"
        icon={<BarChartOutlined />}
        defaultExpanded={false}
        rowKey="key"
        columns={breakdownColumns}
        dataSource={customerQuery.data.customerSegments ?? []}
      />
    </div>
  ) : null;

  const tabItems = [
    { key: 'overview', label: <span><DashboardOutlined /> Overview</span>, children: overviewContent },
    { key: 'sales', label: <span><ShoppingCartOutlined /> Sales</span>, children: salesContent },
    { key: 'inventory', label: <span><InboxOutlined /> Inventory</span>, children: inventoryContent },
    { key: 'customers', label: <span><TeamOutlined /> Customers</span>, children: customerContent },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <div className={styles.toolbarActions}>
            <Button className={styles.headerBtn} icon={<ReloadOutlined />} onClick={resetFilters}>
              Reset
            </Button>
            <Button
              type="primary"
              className={styles.exportBtn}
              icon={<DownloadOutlined />}
              loading={isExporting}
              onClick={handleExport}
            >
              Export XLSX
            </Button>
          </div>
        }
      />

      <div className={styles.toolbar}>
        <div className={styles.toolbarFilters}>
          <div className={styles.filterField}>
            <span className={styles.filterLabel}>Date range</span>
            <DatePicker.RangePicker
              value={dateRangeValue}
              format="YYYY-MM-DD"
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => {
                if (!values?.[0] || !values?.[1]) return;
                patchParams({
                  dateFrom: values[0].format('YYYY-MM-DD'),
                  dateTo: values[1].format('YYYY-MM-DD'),
                });
              }}
              allowClear={false}
            />
          </div>

          <div className={styles.filterField}>
            <span className={styles.filterLabel}>Group by</span>
            <Select
              value={state.groupBy}
              onChange={(value) => patchParams({ groupBy: value })}
              options={[
                { label: 'Daily', value: 'day' },
                { label: 'Weekly', value: 'week' },
                { label: 'Monthly', value: 'month' },
              ]}
              style={{ minWidth: 140 }}
            />
          </div>

          {state.tab === 'inventory' ? (
            <div className={styles.filterField}>
              <span className={styles.filterLabel}>Warehouse</span>
              <Select
                allowClear
                placeholder="All warehouses"
                value={state.warehouseId || undefined}
                onChange={(value) => patchParams({ warehouseId: value ?? '' })}
                options={warehouses.map((warehouse) => ({
                  label: warehouse.name,
                  value: warehouse.id,
                }))}
                style={{ minWidth: 180 }}
              />
            </div>
          ) : null}
        </div>
      </div>

      <Card className={styles.tabsCard} bordered={false}>
        <Tabs
          activeKey={state.tab}
          onChange={(key) => patchParams({ tab: key as ReportTab })}
          items={tabItems}
        />
      </Card>
    </div>
  );
}
